import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Admin() {
  const [key, setKey] = useState(
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("key") || ""
      : ""
  );
  const [keyInput, setKeyInput] = useState(key);
  const [seats, setSeats] = useState({ total: 15, taken: 0, remaining: 15 });
  const [takenInput, setTakenInput] = useState(0);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(false);

  const fetchAll = async (k) => {
    setLoading(true);
    try {
      const seatsRes = await axios.get(`${API}/seats`);
      setSeats(seatsRes.data);
      setTakenInput(seatsRes.data.taken);
      const appsRes = await axios.get(`${API}/admin/applications`, {
        params: { key: k },
      });
      setApplications(appsRes.data || []);
      setAuthed(true);
    } catch (e) {
      setAuthed(false);
      toast.error("Chiave admin non valida");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) fetchAll(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    setKey(keyInput);
    fetchAll(keyInput);
  };

  const handleUpdate = async () => {
    try {
      const r = await axios.put(
        `${API}/admin/seats`,
        { taken: Number(takenInput) },
        { params: { key } }
      );
      setSeats(r.data);
      toast.success(`Posti aggiornati: ${r.data.remaining}/${r.data.total}`);
    } catch (e) {
      toast.error("Errore aggiornamento posti");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#050505] text-white grid place-items-center px-6">
        <form
          onSubmit={handleLogin}
          className="w-full max-w-md rounded-2xl border hairline bg-white/[0.03] p-8"
        >
          <div className="text-[11px] mono tracking-[0.3em] uppercase text-[#9D4CDD]">
            Pannello amministrazione
          </div>
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight">
            Accesso riservato
          </h1>
          <p className="mt-2 text-white/60 text-sm">
            Inserisci la chiave admin per gestire posti e candidature.
          </p>
          <div className="mt-6">
            <Label htmlFor="admin-key" className="text-white/70 text-xs uppercase tracking-[0.2em]">
              Admin key
            </Label>
            <Input
              id="admin-key"
              data-testid="admin-key-input"
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              className="mt-2 bg-black/40 border-white/15 text-white"
              placeholder="••••••••••"
            />
          </div>
          <Button
            data-testid="admin-login"
            type="submit"
            className="mt-6 w-full bg-[#6000FF] hover:bg-[#7A22FF] text-white"
          >
            Entra
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="text-[11px] mono tracking-[0.3em] uppercase text-[#9D4CDD]">
              Admin · Accademia TIR x Mirlo's
            </div>
            <h1 className="mt-3 text-4xl md:text-5xl font-black tracking-tighter">
              Dashboard
            </h1>
          </div>
          <a
            href="/"
            className="text-[11px] mono tracking-[0.3em] uppercase text-white/60 hover:text-white"
          >
            ← Torna al sito
          </a>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="rounded-2xl border hairline bg-white/[0.03] p-7">
            <div className="text-[11px] mono tracking-[0.3em] uppercase text-white/50">
              Totale posti
            </div>
            <div className="mt-3 text-5xl font-black tracking-tighter">
              {seats.total}
            </div>
          </div>
          <div className="rounded-2xl border hairline bg-white/[0.03] p-7">
            <div className="text-[11px] mono tracking-[0.3em] uppercase text-white/50">
              Posti occupati
            </div>
            <div
              data-testid="admin-taken"
              className="mt-3 text-5xl font-black tracking-tighter text-[#9D4CDD]"
            >
              {seats.taken}
            </div>
          </div>
          <div className="rounded-2xl border border-[#6000FF]/50 bg-[#6000FF]/[0.08] p-7">
            <div className="text-[11px] mono tracking-[0.3em] uppercase text-[#9D4CDD]">
              Posti rimasti
            </div>
            <div
              data-testid="admin-remaining"
              className="mt-3 text-5xl font-black tracking-tighter"
            >
              {seats.remaining}
            </div>
          </div>
        </div>

        <div className="mt-10 rounded-2xl border hairline bg-white/[0.03] p-7">
          <div className="text-[11px] mono tracking-[0.3em] uppercase text-white/50">
            Aggiorna posti occupati
          </div>
          <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-end gap-4">
            <div className="flex-1 w-full max-w-xs">
              <Label
                htmlFor="taken"
                className="text-white/70 text-xs uppercase tracking-[0.2em]"
              >
                Posti occupati (0 - {seats.total})
              </Label>
              <Input
                id="taken"
                data-testid="admin-taken-input"
                type="number"
                min={0}
                max={seats.total}
                value={takenInput}
                onChange={(e) => setTakenInput(e.target.value)}
                className="mt-2 bg-black/40 border-white/15 text-white"
              />
            </div>
            <Button
              data-testid="admin-update-seats"
              onClick={handleUpdate}
              className="bg-[#6000FF] hover:bg-[#7A22FF] text-white"
            >
              Aggiorna
            </Button>
          </div>
        </div>

        <div className="mt-12">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-extrabold tracking-tight">
              Candidature ({applications.length})
            </h2>
            <button
              onClick={() => fetchAll(key)}
              className="text-[11px] mono tracking-[0.3em] uppercase text-white/60 hover:text-white"
            >
              {loading ? "…" : "Aggiorna"}
            </button>
          </div>
          <div className="mt-6 rounded-2xl border hairline overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.04] text-white/60 text-[11px] mono tracking-[0.25em] uppercase">
                <tr>
                  <th className="text-left p-4">Data</th>
                  <th className="text-left p-4">Nome</th>
                  <th className="text-left p-4">Contatto</th>
                  <th className="text-left p-4">Note</th>
                </tr>
              </thead>
              <tbody>
                {applications.length === 0 && (
                  <tr>
                    <td className="p-6 text-white/50" colSpan={4}>
                      Nessuna candidatura ricevuta.
                    </td>
                  </tr>
                )}
                {applications.map((a) => (
                  <tr
                    key={a.id}
                    data-testid={`application-row-${a.id}`}
                    className="border-t hairline"
                  >
                    <td className="p-4 text-white/70 mono text-xs">
                      {a.created_at
                        ? new Date(a.created_at).toLocaleString("it-IT")
                        : "-"}
                    </td>
                    <td className="p-4 font-medium">{a.name || "-"}</td>
                    <td className="p-4 text-white/70">
                      {[a.phone, a.email].filter(Boolean).join(" · ") || "-"}
                    </td>
                    <td className="p-4 text-white/60">{a.note || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
